/**
 * SidebarGroupItem Component - Task Group 6, Group Sub-Timeline feature
 *
 * Draggable group item in the sidebar groups section.
 *
 * Features:
 * - Displays group name and tool count badge
 * - Folder icon (Lucide) to distinguish from individual tools
 * - useDrag hook for drag functionality with type TOOL_GROUP
 * - Drag payload includes group ID and full group data
 * - Double-click to enter group edit mode
 * - Right-click context menu for deletion and editing
 * - Hover state with Framer Motion
 */

import { useState, useCallback } from 'react';
import { useDrag } from 'react-dnd';
import { motion } from 'framer-motion';
import * as ContextMenu from '@radix-ui/react-context-menu';
import { Folder, Trash2, Edit3 } from 'lucide-react';
import type { Group } from '../../stores/timelineStore';
import { useTimelineStore } from '../../stores/timelineStore';
import { useUiStore } from '../../stores/uiStore';
import { DND_ITEM_TYPES } from './dndTypes';
import type { GroupDragItem } from './dndTypes';
import { ConfirmDialog } from '../dialogs/ConfirmDialog';

interface SidebarGroupItemProps {
  /**
   * Group data from timelineStore
   */
  group: Group;
}

/**
 * Background color for group items (neutral gray)
 */
const GROUP_BG_COLOR = '#4a5568';
const GROUP_HOVER_COLOR = '#5a6578';

export function SidebarGroupItem({ group }: SidebarGroupItemProps) {
  // State for delete confirmation dialog
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Get actions from stores
  const deleteGroup = useTimelineStore((state) => state.deleteGroup);
  const enterGroupEditMode = useUiStore((state) => state.enterGroupEditMode);

  /**
   * Handle double-click to enter group edit mode
   * Allows editing group's internal tools without placing it on timeline
   */
  const handleDoubleClick = useCallback(() => {
    enterGroupEditMode(group.id);
  }, [enterGroupEditMode, group.id]);

  // Set up drag behavior
  const [{ isDragging }, drag] = useDrag<
    GroupDragItem,
    unknown,
    { isDragging: boolean }
  >(
    () => ({
      type: DND_ITEM_TYPES.TOOL_GROUP,
      item: {
        type: DND_ITEM_TYPES.TOOL_GROUP,
        groupId: group.id,
        group: group,
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [group]
  );

  /**
   * Handle delete confirmation
   */
  const handleDeleteConfirm = () => {
    deleteGroup(group.id);
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <ContextMenu.Root>
        <ContextMenu.Trigger asChild>
          <motion.div
            ref={drag as any}
            className="relative cursor-move select-none rounded p-3 mb-2"
            style={{
              backgroundColor: GROUP_BG_COLOR,
              opacity: isDragging ? 0.5 : 1,
              boxShadow: 'var(--shadow-sm)',
            }}
            whileHover={{
              scale: 1.02,
              backgroundColor: GROUP_HOVER_COLOR,
              boxShadow: 'var(--shadow-md)',
              transition: { duration: 0.15 },
            }}
            whileTap={{
              scale: 0.98,
              transition: { duration: 0.1 },
            }}
            onDoubleClick={handleDoubleClick}
            data-testid={`sidebar-group-${group.id}`}
            data-group-id={group.id}
          >
            {/* Folder icon and group name row */}
            <div className="flex items-center gap-2">
              {/* Folder icon */}
              <Folder
                className="h-4 w-4 flex-shrink-0"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                }}
              />

              {/* Group name */}
              <div
                className="text-sm font-medium flex-1 truncate"
                style={{
                  color: 'rgba(255, 255, 255, 0.95)',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                }}
              >
                {group.name}
              </div>

              {/* Tool count badge */}
              <div
                className="flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-medium"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: 'rgba(255, 255, 255, 0.85)',
                }}
              >
                {group.tools.length}
              </div>
            </div>

            {/* Drag indicator icon */}
            <div
              className="absolute right-2 top-1/2 -translate-y-1/2 opacity-40"
              style={{
                color: 'rgba(255, 255, 255, 0.8)',
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="5" cy="4" r="1.5" />
                <circle cx="11" cy="4" r="1.5" />
                <circle cx="5" cy="8" r="1.5" />
                <circle cx="11" cy="8" r="1.5" />
                <circle cx="5" cy="12" r="1.5" />
                <circle cx="11" cy="12" r="1.5" />
              </svg>
            </div>
          </motion.div>
        </ContextMenu.Trigger>

        {/* Context menu for group actions */}
        <ContextMenu.Portal>
          <ContextMenu.Content
            className="min-w-[160px] rounded-md border border-neutral-700 bg-neutral-800 p-1 shadow-lg"
            style={{ zIndex: 'var(--z-index-dropdown)' }}
          >
            {/* Edit */}
            <ContextMenu.Item
              onSelect={handleDoubleClick}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-white outline-none hover:bg-neutral-700 focus:bg-neutral-700"
            >
              <Edit3 className="h-4 w-4" />
              Edit Group
            </ContextMenu.Item>

            <ContextMenu.Separator className="my-1 h-px bg-neutral-700" />

            {/* Delete */}
            <ContextMenu.Item
              onSelect={() => setShowDeleteConfirm(true)}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-red-400 outline-none hover:bg-red-900/20 focus:bg-red-900/20"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </ContextMenu.Item>
          </ContextMenu.Content>
        </ContextMenu.Portal>
      </ContextMenu.Root>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Group"
        description={`Delete group '${group.name}'? This cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleDeleteConfirm}
        destructive={true}
      />
    </>
  );
}
